/**
 * Checkout custom-image sync orchestrator (WIL26D2C-329). Loaded on every
 * page so it can catch an add-to-cart anywhere (quick-add, PDP, cart
 * drawer). Two entry points into the same sync: a page-load JSON snapshot
 * (snippets/rogue-golf-image-sync.liquid, only emitted when already
 * pending) and live CartAddEvent/CartUpdateEvent updates. Sends pending
 * image URLs to the proxy, writes back a CDN URL on success or a backoff on
 * failure — see assets/rogue-golf-core.js.
 */

import { collectPendingLines, collectImageUrls, matchResults } from '@theme/rogue-golf-core';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';

const PROXY_ENDPOINT = '/apps/rogue-golf/images/process';
const DATA_SELECTOR = 'script[data-rogue-golf-cart]';

async function postJson(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`${url} responded ${response.status}`);
  }

  return response.json();
}

// Liquid's `item.properties | json` renders as `{}` or an array of [key,
// value] pairs; the Ajax Cart API always returns a plain object.
function normalizeProperties(properties) {
  return Array.isArray(properties) ? Object.fromEntries(properties) : (properties ?? {});
}

function itemsFromCart(cart) {
  return (cart?.items ?? []).map((item) => ({
    key: item.key,
    quantity: item.quantity,
    properties: normalizeProperties(item.properties),
  }));
}

function itemsFromSnapshot() {
  const dataElement = document.querySelector(DATA_SELECTOR);
  if (!dataElement) return null;

  const rawItems = JSON.parse(dataElement.textContent);
  return rawItems.map((item) => ({ ...item, properties: normalizeProperties(item.properties) }));
}

async function syncCartImages(items) {
  if (!items?.length) return;

  // Computed once and threaded through both calls below, so the pending
  // check and the backoff timestamps this round writes agree on "now" —
  // avoids a line looking eligible in one call and not the other purely
  // because a few milliseconds ticked by between them.
  const now = Date.now();
  const pendingLines = collectPendingLines(items, now);
  if (pendingLines.length === 0) return;

  const { results } = await postJson(PROXY_ENDPOINT, { urls: collectImageUrls(pendingLines) });
  const { changes } = matchResults(pendingLines, results, now);

  if (changes.length === 0) return;

  let cart;

  // change.js replaces the whole properties object and takes one line per
  // call — run sequentially to avoid cart version conflicts. Every pending
  // line gets a change now, success or backoff, not just the successful
  // ones.
  for (const change of changes) {
    cart = await postJson(Theme.routes.cart_change_url, change);
  }

  // No `sections` payload — component-cart-items.js falls back to a live
  // re-fetch when it's absent.
  document.dispatchEvent(
    new CartUpdateEvent(cart, 'rogue-golf-image-sync', {
      source: 'rogue-golf-image-sync',
      itemCount: cart?.item_count,
    }),
  );
}

function runSync(items) {
  syncCartImages(items).catch((error) => {
    console.warn('[rogue-golf] image sync failed; will retry on next page load', error);
  });
}

const snapshotItems = itemsFromSnapshot();
if (snapshotItems) runSync(snapshotItems);

// CartAddEvent shares CartUpdateEvent's DOM event, including our own
// dispatch above — skip self-caused/errored updates to avoid looping.
/**
 * @param {CustomEvent<{ resource?: { items?: object[] }, data?: { source?: string, didError?: boolean } }>} event
 */
document.addEventListener(CartAddEvent.eventName, (event) => {
  if (event.detail?.data?.source === 'rogue-golf-image-sync' || event.detail?.data?.didError) return;
  const cart = event.detail?.resource;
  if (!cart?.items) return;
  runSync(itemsFromCart(cart));
});
